import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Chip,
  makeStyles,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { formattedCurrency, formatStockAmount, capitalize } from '../../../utils/utils';

const useStyles = makeStyles((theme) => ({
  card: {
    border: '1px solid #e0e6ed',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: '#b2c8e2',
      boxShadow: '0 3px 8px rgba(25, 118, 210, 0.08)',
    },
    position: 'relative',
    height: '100%',
  },
  content: {
    padding: theme.spacing(1.8),
    '&:last-child': {
      paddingBottom: theme.spacing(1.8),
    },
  },
  title: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: '#2c3e50',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  priceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    marginTop: 6,
  },
  price: {
    fontSize: '1.15rem',
    fontWeight: 700,
    color: '#1a73e8',
  },
  cost: {
    fontSize: '0.8rem',
    color: '#6c757d',
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 8,
  },
}));

const ProductSaleFormatCard = ({ format, onEdit, onDelete }) => {
  const classes = useStyles();

  const factor = parseFloat(format.fractionFactor) || 1;
  const cost = parseFloat(format.cost) || 0;
  const value = parseFloat(format.value) || 0;
  const profit = value - cost;
  const margin = value > 0 ? ((profit / value) * 100).toFixed(1) : 0;

  return (
    <Card className={classes.card} variant="outlined">
      <CardContent className={classes.content}>
        <Box className={classes.title}>
          <Box display="flex" alignItems="center" gridGap={6}>
            <span>{format.name}</span>
          </Box>
          <Box>
            {onEdit && (
              <Tooltip title="Editar formato">
                <IconButton size="small" onClick={() => onEdit(format)} style={{ padding: 4, marginRight: 2 }}>
                  <EditIcon fontSize="small" style={{ color: '#5f6368' }} />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && !format.id && (
              <Tooltip title="Remover formato">
                <IconButton size="small" onClick={() => onDelete(format)} style={{ padding: 4 }}>
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box className={classes.priceRow}>
          <Typography className={classes.price}>
            {formattedCurrency(value)}
            <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 500, marginLeft: 4 }}>
              / {format.unit || 'un'}
            </span>
          </Typography>
          <Typography className={classes.cost}>
            Custo: {formattedCurrency(cost)}
          </Typography>
        </Box>

        <Box className={classes.chipsRow}>
          <Chip
            size="small"
            label={`1 de ${formatStockAmount(factor)} ${format.unit || 'un'}`}
            style={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '0.75rem' }}
          />
          <Chip
            size="small"
            label={`Lucro: ${formattedCurrency(profit)} (${margin}%)`}
            style={{
              backgroundColor: profit >= 0 ? '#e8f5e9' : '#ffebee',
              color: profit >= 0 ? '#2e7d32' : '#c62828',
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          />
          {format.barCode && (
            <Chip
              size="small"
              variant="outlined"
              label={`Cód: ${format.barCode}`}
              style={{ fontSize: '0.75rem', color: '#555' }}
            />
          )}
          {format.categoryName && (
            <Chip
              size="small"
              variant="outlined"
              label={`Cat: ${format.categoryName}`}
              style={{
                fontSize: '0.75rem',
                borderColor: '#bfdbfe',
                color: '#1e40af',
                backgroundColor: '#eff6ff',
                fontWeight: 500,
              }}
            />
          )}
          {format.particulars &&
            typeof format.particulars === 'object' &&
            Object.entries(format.particulars).map(([k, v]) =>
              v ? (
                <Chip
                  key={k}
                  size="small"
                  variant="outlined"
                  label={`${capitalize(k)}: ${v}`}
                  style={{
                    fontSize: '0.75rem',
                    borderColor: '#93c5fd',
                    color: '#17497F',
                    backgroundColor: '#eff6ff',
                  }}
                />
              ) : null,
            )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProductSaleFormatCard;
